import type { ReactNode } from "react";

export type WelcomeHeaderProps = {
  titleLines: [string, string];
  description: string;
  notification: {
    appLabel: ReactNode;
    title: string;
    timeLabel: string;
    body: string;
  };
};

export function WelcomeHeader({
  titleLines,
  description,
  notification,
}: WelcomeHeaderProps) {
  return (
    
    <header
      className="overflow-hidden relative rounded-md px-6 py-8 text-white sm:px-8"
      style={{
        background:
          "linear-gradient(90deg, rgba(11,63,116,1) 0%, rgba(11,63,116,1) 52%, rgba(23,102,170,1) 100%)",
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-[520px]">
          <h1 className="text-[22px] font-semibold leading-[1.15] text-white">
            {titleLines[0]}
            <br />
            {titleLines[1]}
          </h1>
          <p className="mt-3 text-sm font-medium leading-relaxed text-white/90">
            {description}
          </p>
        </div>

        <div className="absolute top-0 right-0 hidden lg:block">
          <svg
            width="1055"
            height="240"
            viewBox="0 0 1055 240"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_12_12366)">
              <path
                d="M699.524 68.2298C876.558 -30.0842 1052.56 -114.992 1224.78 -103.304L1228.56 -185.462C1056.34 -193.024 880.339 -113.617 703.649 -17.709C702.274 10.8227 701.243 39.6981 699.867 68.2298H699.524Z"
                fill="url(#paint0_linear_12_12366)"
              />
              <path
                d="M1240.23 103.981C1238.85 132.169 1237.82 160.701 1236.45 189.233C860.38 134.232 460.249 623.395 84.1804 568.394L100.337 485.893C472.624 529.206 868.286 45.8867 1240.23 103.981Z"
                fill="url(#paint1_linear_12_12366)"
              />
              <path
                d="M1311.43 269.668C1310.05 298.544 1309.02 327.075 1307.64 355.951C1095.55 274.137 873.482 409.577 652.104 527.828C431.757 649.174 212.441 753.676 0 700.737L16.1565 618.58C436.913 744.738 891.357 121.51 1311.43 269.668Z"
                fill="url(#paint2_linear_12_12366)"
              />
              <path
                d="M1199.01 36.9482C978.665 25.9481 750.412 188.2 524.221 306.796L528.002 219.826C754.193 101.23 982.103 -55.8657 1202.79 -46.5843C1201.42 -19.0839 1200.39 8.7603 1199.01 36.9482Z"
                fill="url(#paint3_linear_12_12366)"
              />
            </g>
            <defs>
              <linearGradient
                id="paint0_linear_12_12366"
                x1="1444.16"
                y1="-145.996"
                x2="1221.35"
                y2="372.908"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="white" />
                <stop offset="1" stop-color="white" stop-opacity="0" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_12_12366"
                x1="1711.36"
                y1="173.586"
                x2="1341.69"
                y2="1183.29"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="white" />
                <stop offset="1" stop-color="white" stop-opacity="0" />
              </linearGradient>
              <linearGradient
                id="paint2_linear_12_12366"
                x1="1845.88"
                y1="320.337"
                x2="1518.19"
                y2="1347.59"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="white" />
                <stop offset="1" stop-color="white" stop-opacity="0" />
              </linearGradient>
              <linearGradient
                id="paint3_linear_12_12366"
                x1="1479.34"
                y1="8.644"
                x2="1151.89"
                y2="711.431"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="white" />
                <stop offset="1" stop-color="white" stop-opacity="0" />
              </linearGradient>
              <clipPath id="clip0_12_12366">
                <rect
                  width="1311.43"
                  height="1957"
                  fill="white"
                  transform="translate(0 -709)"
                />
              </clipPath>
            </defs>
          </svg>
        </div>

        <div className="w-full max-w-[410px] rounded-2xl border border-white/35 bg-white/[0.65] p-4 text-slate-900 shadow-[0_10px_25px_rgba(2,6,23,0.25)] backdrop-blur-md">
          <div className="flex items-start gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#0b4f93] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)]">
              <div className="text-[8px] font-semibold leading-none">
                {typeof notification.appLabel === "string"
                  ? notification.appLabel.split("\n").map((line) => (
                      <span key={line}>
                        {line}
                        <br />
                      </span>
                    ))
                  : notification.appLabel}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="text-[12px] font-semibold leading-none text-slate-900">
                  {notification.title}
                </div>
                <div className="text-[10px] font-medium leading-none text-slate-600">
                  {notification.timeLabel}
                </div>
              </div>

              <div className="mt-2 text-[11px] leading-relaxed text-slate-900">
                {notification.body}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
